import {Map, List} from 'immutable';

import RESTClient from 'web-app/api/clients/rest-client-famly-app';
import Child from 'web-app/react/entities/children/model';
import BehaviorsCollection from 'web-app/behaviors/behaviors-collection';

import BillPayer, {SensitiveInfo} from './model';
import SplitConfiguration from './common/split-configuration';

export const fetchAllBillPayers = (institutionId, childId, {includeRecipients} = {includeRecipients: undefined}) => {
    return RESTClient.getAuthenticatedObservable(`v2/invoicing/${institutionId}/${childId}/billPayers`, {
        includeRecipients,
    }).map(response => ({
        billPayers: List(response.billPayers.map(BillPayer.fromAPI)),
        splitConfiguration: SplitConfiguration.fromAPI(response.billPayerShares),
        behaviors: new BehaviorsCollection(response.behaviors),
        recipients: List(response.recipients.map(BillPayer.fromAPI)),
    }));
};

export const fetchBillPayers = (
    targetId: string,
    limit: number,
    next,
    asOf: string,
    includeDeleted: boolean,
    onlyMissingAddress: boolean,
    paymentSourceFilter: Map<any, any>,
    includeCreditNotesAndCancelledInvoicesInBalance: boolean,
) => {
    return RESTClient.getAuthenticatedObservable(`v2/invoicing/billPayers/${targetId}`, {
        limit,
        next,
        asOf,
        includeDeleted,
        onlyMissingAddress,
        paymentSourceFilter,
        includeCreditNotesAndCancelledInvoicesInBalance,
    }).map(response => ({
        billPayers: List(response.billPayers.map(BillPayer.fromAPI)),
        balances: Map(response.balances),
        next: response.next,
        csv: response.csv,
    }));
};

export const searchBillPayer = (institutionId, childId, term, billPayerType) => {
    return RESTClient.getAuthenticatedObservable(`v2/invoicing/${institutionId}/billPayers/search`, {
        childId,
        term,
        billPayerType,
    }).map(response => ({
        billPayers: List(response.billPayers.map(BillPayer.fromAPI)),
        children: Object.values(response.children).map(Child.fromAPI),
    }));
};

export const createBillPayer = (institutionId, billPayer) => {
    return RESTClient.postAuthenticatedObservable(`v2/invoicing/${institutionId}/billPayers`, {
        billPayer,
    }).map(response => BillPayer.fromAPI(response.billPayer));
};

export const getBillPayer = (institutionId, billPayerId) => {
    return RESTClient.getAuthenticatedObservable(`v2/invoicing/${institutionId}/billPayers/${billPayerId}`).map(
        response => ({
            billPayer: BillPayer.fromAPI(response.billPayer),
            children: Object.values(response.children).map(Child.fromAPI),
        }),
    );
};

export const discontinueBillPayer = (institutionId, billPayerId) => {
    return RESTClient.deleteAuthenticatedObservable(`v2/invoicing/${institutionId}/billPayers/${billPayerId}`).map(
        response => ({
            billPayer: BillPayer.fromAPI(response.billPayer),
        }),
    );
};

export const reviveBillPayer = (institutionId, billPayerId) => {
    return RESTClient.postAuthenticatedObservable(`v2/invoicing/${institutionId}/billPayers/${billPayerId}/revive`).map(
        response => ({
            billPayer: BillPayer.fromAPI(response.billPayer),
        }),
    );
};

export const updateBillPayer = (institutionId, billPayerId, billPayer) => {
    return RESTClient.putAuthenticatedObservable(`v2/invoicing/${institutionId}/billPayers/${billPayerId}`, {
        billPayer,
    }).map(response => BillPayer.fromAPI(response.billPayer));
};

export const updateSplit = (institutionId, childId, splitConfiguration) => {
    return RESTClient.postAuthenticatedObservable(
        `v2/invoicing/${institutionId}/${childId}/billPayerSplit`,
        SplitConfiguration.toAPI(splitConfiguration),
    ).map(response => SplitConfiguration.fromAPI(response.billPayerShares));
};

export const updateNote = (institutionId, notes) => {
    return RESTClient.postAuthenticatedObservable('v2/invoicing/bill-payers/notes', {notes}).map(response =>
        List(response.billPayers).map(bp => BillPayer.fromAPI(bp)),
    );
};

export const fetchSensitiveInformation = (institutionId, billPayerId) => {
    return RESTClient.getAuthenticatedObservable(
        `v2/invoicing/${institutionId}/billPayers/${billPayerId}/sensitive`,
    ).map(response => new SensitiveInfo(response));
};

export const updateSensitiveInformation = (institutionId, billPayerId, sensitiveInformation) => {
    return RESTClient.postAuthenticatedObservable(
        `v2/invoicing/${institutionId}/billPayers/${billPayerId}/sensitive`,
        sensitiveInformation,
    );
};
